import { IErrorMessage } from '../../ws_result.model';

export namespace FornitoriAdmin {
  // ── find_supplier ─────────────────────────────────────────────────────────

  export interface IFindSupplierResult {
    codice_fornitore: number;
    fornitore: string;
    fornitore_mail: string;
    fornitore_telefono: string;
  }

  export class FindSupplierResult implements IFindSupplierResult {
    codice_fornitore = 0;
    fornitore = '';
    fornitore_mail = '';
    fornitore_telefono = '';

    constructor(data?: Partial<IFindSupplierResult>) {
      if (!data) return;
      Object.assign(this, data);
    }
  }

  // ── save_supplier ─────────────────────────────────────────────────────────

  export interface ISaveSupplierInput {
    id_fornitore: number;
    account_id: number;
    codice_fornitore: number;
    fornitore: string;
    fornitore_mail: string;
    fornitore_telefono: string;
    ac_nome: string;
    ac_cognome: string;
    ac_password: string;
    ac_tipo_utente_cod: number;
    ac_note: string;
  }

  export class SaveSupplierInput implements ISaveSupplierInput {
    id_fornitore = 0;
    account_id = 0;
    codice_fornitore = 0;
    fornitore = '';
    fornitore_mail = '';
    fornitore_telefono = '';
    ac_nome = '';
    ac_cognome = '';
    ac_password = '';
    ac_tipo_utente_cod = 4;
    ac_note = '';

    constructor(data?: Partial<ISaveSupplierInput>) {
      if (!data) return;
      Object.assign(this, data);
    }
  }

  // ── status_supplier ───────────────────────────────────────────────────────

  export interface IStatusSupplierInput {
    id_fornitore: number;
    account_id: number;
    ac_attivo: 'S' | 'N';
  }

  export class StatusSupplierInput implements IStatusSupplierInput {
    id_fornitore = 0;
    account_id = 0;
    ac_attivo: 'S' | 'N' = 'N';

    constructor(data?: Partial<IStatusSupplierInput>) {
      if (!data) return;
      Object.assign(this, data);
    }
  }

  // ── get_supplier_open_orders ──────────────────────────────────────────────

  export interface IOpenOrder {
    numero_ordine: string;
    or_numero_ordine: string;
    or_fornitore: string;
    or_codice_fornitore: string;
    or_data_ordine: string;
    or_data_assegnazione: string;
    or_data_chiusura: string | null;
    or_data_arrivo: string | null;
    num_prodotti: number;
    num_pezzi: number;
    ultimo_stato_perc: number;
    sollecito: boolean;
    ordine_passivo_id: number;
    or_gamma_id: number;
    fornitore_certificato_id: number | null;
  }

  export class OpenOrder implements IOpenOrder {
    numero_ordine = '';
    or_numero_ordine = '';
    or_fornitore = '';
    or_codice_fornitore = '';
    or_data_ordine = '';
    or_data_assegnazione = '';
    or_data_chiusura: string | null = null;
    or_data_arrivo: string | null = null;
    num_prodotti = 0;
    num_pezzi = 0;
    ultimo_stato_perc = 0;
    sollecito = false;
    ordine_passivo_id = 0;
    or_gamma_id = 0;
    fornitore_certificato_id: number | null = null;

    constructor(data?: any) {
      if (!data) return;
      this.numero_ordine = data.numero_ordine ?? '';
      this.or_numero_ordine =
        data.or_numero_ordine ?? String(data.or_gamma_id ?? '');
      this.or_fornitore = data.or_fornitore ?? data.fornitore ?? '';
      this.or_codice_fornitore =
        data.or_codice_fornitore ?? data.codice_fornitore ?? '';
      this.or_data_ordine = data.or_data_ordine ?? data.data_ordine ?? '';
      this.or_data_assegnazione =
        data.or_data_assegnazione ?? data.data_consegna ?? '';
      // 🚨 FIX: `fc_data_chiusura` è la chiusura del SINGOLO certificato
      // fornitore (fornitore_certificato_id), non dell'ordine. Un ordine
      // può avere più cicli di certificazione (id diversi) ognuno chiuso
      // singolarmente mentre l'ordine stesso resta "in lavorazione" —
      // usarlo come fallback marcava l'ordine come chiuso per errore.
      this.or_data_chiusura = data.or_data_chiusura ?? null;
      this.or_data_arrivo = data.or_data_arrivo ?? data.data_arrivo ?? null;
      this.num_prodotti = data.num_prodotti ?? 0;
      this.num_pezzi = data.num_pezzi ?? 0;
      this.ultimo_stato_perc =
        data.ultimo_stato_perc ??
        (parseFloat(String(data.percentuale ?? '0').replace('%', '')) || 0);
      this.sollecito = data.sollecito ?? false;
      this.ordine_passivo_id = data.ordine_passivo_id ?? data.id_ordine ?? 0;
      this.or_gamma_id = data.or_gamma_id ?? data.id_gamma ?? 0;
      this.fornitore_certificato_id = data.fornitore_certificato_id;
    }
  }

  // ── Shared operation result (save / status) ───────────────────────────────

  export interface ISupplierOperationResult {
    success: boolean;
    error_message: IErrorMessage;
    id_result: number;
  }

  export class SupplierOperationResult implements ISupplierOperationResult {
    success = false;
    error_message: IErrorMessage = { msg_type: '', msg_code: 0, msg_text: '' };
    id_result = 0;

    constructor(data?: Partial<ISupplierOperationResult>) {
      if (!data) return;
      Object.assign(this, data);
    }
  }

  // ── get_suppliers_list ────────────────────────────────────────────────────

  export interface IGetSuppliersListInput {
    search?: string | null;
    attivo?: 'S' | 'N' | null;
    page?: number;
    page_size?: number;
  }

  export interface ISupplierListItem {
    fornitore_id: number;
    account_id: number | null;
    codice_fornitore: string;
    fornitore: string;
    fornitore_mail: string;
    fornitore_telefono: string;
    fn_attivo: 'S' | 'N';
    ac_attivo: 'S' | 'N' | '';
    ac_nome: string;
    ac_cognome: string;
    ac_data_scadenza: string | null;
  }

  export interface IGetSuppliersListResponse {
    total: number;
    page: number;
    page_size: number;
    items: ISupplierListItem[];
  }

  export class GetSuppliersListResponse implements IGetSuppliersListResponse {
    total = 0;
    page = 1;
    page_size = 50;
    items: ISupplierListItem[] = [];

    constructor(data?: Partial<IGetSuppliersListResponse>) {
      if (!data) return;
      this.total = data.total ?? 0;
      this.page = data.page ?? 1;
      this.page_size = data.page_size ?? 50;
      this.items = Array.isArray(data.items) ? data.items : [];
    }

    get totalPages(): number {
      return this.page_size > 0 ? Math.ceil(this.total / this.page_size) : 1;
    }
  }

  // ── get_supplier_open_orders_details ─────────────────────────────────────

  export interface IOrderDetailDocument {
    supplier_certificate_document_id: number;
    document_url: string;
    document_description: string;
    // Validazione certificato (Quality Certifier) — tri-state:
    // 'S' validato positivo, 'N' validato negativo, '' / null non verificato.
    document_check: 'S' | 'N' | '' | null;
    document_check_operator: string | null;
    document_check_operator_id: number | null;
    document_check_date: string | null;
  }

  export interface IOrderDetailBatch {
    supplier_certificate_batch_id: number;
    batch_year: number;
    batch_week: number;
    serial_from: number;
    serial_to: number;
    qta: number;
    quantity?: number;
    documents: IOrderDetailDocument[];
  }

  export interface IOrderDetailProduct {
    supplier_certificate_product_id: number;
    fornitore_certificato_prodotti_id: number;
    product_code: string;
    product_description: string;
    fornitore_certificato_id: number;
    quantity: number;
    is_prepared: boolean;
    batches: IOrderDetailBatch[];
  }

  export interface IOrderDetail {
    supplier_certificate_id: number;
    or_numero_ordine: string;
    or_fornitore: string;
    or_codice_fornitore: string;
    or_data_ordine: string;
    or_data_assegnazione: string;
    or_data_chiusura: string | null;
    or_data_arrivo: string | null;
    ddt_url: string | null;
    ddt_file: string | null;
    ddt_date: string | null;
    products: IOrderDetailProduct[];
  }

  export class OrderDetail implements IOrderDetail {
    supplier_certificate_id = 0;
    or_numero_ordine = '';
    or_fornitore = '';
    or_codice_fornitore = '';
    or_data_ordine = '';
    or_data_assegnazione = '';
    or_data_chiusura: string | null = null;
    or_data_arrivo: string | null = null;
    or_data_consegna: string | null = null;
    ddt_url: string | null = null;
    ddt_file: string | null = null;
    ddt_date: string | null = null;
    products: IOrderDetailProduct[] = [];

    constructor(data?: any) {
      if (!data) return;
      this.supplier_certificate_id =
        data.supplier_certificate_id ?? data.fornitore_certificato_id ?? data.id ?? 0;
      this.or_numero_ordine =
        data.or_numero_ordine ?? data.order_number ?? data.numero_ordine ?? '';
      this.or_fornitore = data.or_fornitore ?? data.fornitore ?? '';
      this.or_codice_fornitore =
        data.or_codice_fornitore ?? data.codice_fornitore ?? '';
      this.or_data_ordine = data.or_data_ordine ?? data.order_date ?? '';
      this.or_data_assegnazione =
        data.or_data_assegnazione ?? data.delivery_date ?? '';
      this.or_data_chiusura =
        data.or_data_chiusura ?? data.closing_date ?? null;
      this.or_data_arrivo = data.or_data_arrivo ?? data.data_arrivo ?? null;
      this.or_data_consegna = data.data_consegna ?? data.data_consegna ?? null;
      this.ddt_url = data.ddt_fornitore_url ?? data.ddt_url ?? null;
      this.ddt_file = data.ddt_file ?? null;
      this.ddt_date = data.ddt_date ?? null;
      const rawProducts = Array.isArray(data.products)
        ? data.products
        : Array.isArray(data.prodotti)
          ? data.prodotti
          : [];
      this.products = rawProducts.map(
        (p: any): IOrderDetailProduct => ({
          supplier_certificate_product_id:
            p.supplier_certificate_product_id ??
            p.fornitore_certificato_prodotti_id ??
            0,
          fornitore_certificato_prodotti_id:
            p.fornitore_certificato_prodotti_id ??
            p.supplier_certificate_product_id ??
            0,
          fornitore_certificato_id:
            p.fornitore_certificato_id ?? this.supplier_certificate_id ?? 0,
          product_code: p.product_code ?? p.codice_articolo ?? '',
          product_description: p.product_description ?? p.descrizione ?? '',
          quantity: p.quantity ?? p.qta ?? 0,
          is_prepared: p.is_prepared ?? p.certificati ?? false,
          batches: (Array.isArray(p.batches)
            ? p.batches
            : Array.isArray(p.lotti)
              ? p.lotti
              : []
          ).map(
            (b: any): IOrderDetailBatch => ({
              supplier_certificate_batch_id:
                b.supplier_certificate_batch_id ??
                b.fornitore_certificato_lotto_id ??
                0,
              batch_year: b.batch_year ?? b.anno ?? 0,
              batch_week: b.batch_week ?? b.settimana ?? 0,
              serial_from: b.serial_from ?? b.seriale_da ?? 0,
              serial_to: b.serial_to ?? b.seriale_a ?? 0,
              qta: b.qta ?? b.quantity ?? 0,
              quantity: b.quantity ?? b.qta ?? 0,
              documents: Array.isArray(b.documents)
                ? b.documents
                : Array.isArray(b.certificati)
                  ? b.certificati.map(
                      (d: any): IOrderDetailDocument => ({
                        supplier_certificate_document_id:
                          d.supplier_certificate_document_id ?? d.id ?? 0,
                        document_url: b.document_url ?? d.url ?? '',
                        document_description:
                          d.document_description ?? d.descrizione ?? '',
                        document_check: d.document_check ?? d.check ?? null,
                        document_check_operator:
                          d.document_check_operator ?? null,
                        document_check_operator_id:
                          d.document_check_operator_id ?? null,
                        document_check_date: d.document_check_date ?? null,
                      }),
                    )
                  : [],
            }),
          ),
        }),
      );
    }
  }

  // ── Traceability ──────────────────────────────────────────────────────────

  export interface ITraceProduct {
    pr_codice: string;
    pr_descrizione: string;
    pr_attivo: boolean;
  }

  export class TraceProduct implements ITraceProduct {
    pr_codice = '';
    pr_descrizione = '';
    pr_attivo = false;

    constructor(data?: any) {
      if (!data) return;
      this.pr_codice = data.pr_codice ?? data.product_code ?? '';
      this.pr_descrizione =
        data.pr_descrizione ?? data.product_description ?? '';
      this.pr_attivo = data.pr_attivo ?? true;
    }
  }

  export interface ITraceSupplier {
    codice_fornitore: string;
    fornitore: string;
  }

  export class TraceSupplier implements ITraceSupplier {
    codice_fornitore = '';
    fornitore = '';

    constructor(data?: any) {
      if (!data) return;
      this.codice_fornitore = data.codice_fornitore ?? data.supplier_code ?? '';
      this.fornitore = data.fornitore ?? data.supplier_name ?? '';
    }
  }

  export interface ITraceBatch {
    fcp_anno: number;
    fcp_settimana: number;
    lot: string;
  }

  export class TraceBatch implements ITraceBatch {
    fcp_anno = 0;
    fcp_settimana = 0;
    lot = '';

    constructor(data?: any) {
      if (!data) return;
      this.fcp_anno = data.fcp_anno ?? data.batch_year ?? 0;
      this.fcp_settimana = data.fcp_settimana ?? data.batch_week ?? 0;
      this.lot = data.lot ?? '';
    }
  }

  export interface ITraceSerial {
    fcp_seriale_da: number;
    fcp_seriale_a: number;
    fcp_qta: number;
  }

  export class TraceSerial implements ITraceSerial {
    fcp_seriale_da = 0;
    fcp_seriale_a = 0;
    fcp_qta = 0;

    constructor(data?: any) {
      if (!data) return;
      this.fcp_seriale_da = data.fcp_seriale_da ?? data.serial_from ?? 0;
      this.fcp_seriale_a = data.fcp_seriale_a ?? data.serial_to ?? 0;
      this.fcp_qta = data.fcp_qta ?? data.qty ?? 0;
    }
  }

  export interface ITraceSearchInput {
    art_qr?: string;
    art_code?: string;
    date_from?: string;
    date_to?: string;
  }

  export class TraceSearchInput implements ITraceSearchInput {
    art_qr?: string;
    art_code?: string;
    date_from?: string;
    date_to?: string;

    constructor(data?: Partial<ITraceSearchInput>) {
      if (!data) return;
      Object.assign(this, data);
    }
  }

  export interface ITraceSearchResult {
    order_number: string;
    product_code: string;
    codice_fornitore: string;
    product_qr: string;
    product_description: string;
    batch_year: number;
    batch_week: number;
    serial: number;
    supplier_code: string;
  }

  export class TraceSearchResult implements ITraceSearchResult {
    order_number = '';
    product_code = '';
    codice_fornitore = '';
    product_qr = '';
    product_description = '';
    batch_year = 0;
    batch_week = 0;
    serial = 0;
    supplier_code = '';

    constructor(data?: Partial<ITraceSearchResult>) {
      if (!data) return;
      Object.assign(this, data);
      this.supplier_code = this.extractSupplierCode(this.product_qr);
    }

     private extractSupplierCode(qr: string): string {
        return qr?.split('$')?.[1] ?? '';
     }
  }

  // ── load_trace_product ────────────────────────────────────────────────────

  export interface ITraceHistoryFile {
    document_url: string;
    document_description: string;
    // Validazione certificato (stessi valori salvati dal Quality
    // Certifier lato Purchase Order Details — vedi IOrderDetailDocument).
    check: 'S' | 'N' | '' | null;
    quality_certifier: string | null;
    check_date: string | null;
  }

  export interface ITraceHistorySupplier {
    order_date: string;
    order_number: string;
    name: string;
  }

  export interface ITraceHistoryCertification {
    start_date: string;
    closing_date: string | null;
    ddt_url: string | null;
    ddt_date: string | null;
    files: ITraceHistoryFile[];
  }

  export interface ITraceHistoryWarehouse {
    start_date: string;
    operator: string;
    ddt_url: string | null;
    ddt_date: string | null;
    qc_date: string | null;
    qc_result: string | null;
    closing_date: string | null;
  }

  export interface ITraceHistoryExpedition {
    start_date: string;
    operator: string;
    ddt_url: string | null;
    ddt_date: string | null;
    dop_url: string | null;
    dop_date: string | null;
    product_code: string;
    product_description: string;
    category: string;
    aisle: number | null;
    shelf: number | null;
    ledge: number | null;
    closing_date: string | null;
    box_type: string;
    box_number: number | null;
    box_start_date: string | null;
    expedition_date: string | null;
    courier: string;
    customer: string;
    address: string;
    city: string;
    CAP: string;
    prov: string;
    state: string;
    note: string;
  }

  export interface ITraceHistoryTrackingEvent {
    status: string;
    location: string;
    date: string;
  }

  export interface ITraceHistoryTracking {
    events: ITraceHistoryTrackingEvent[];
  }

  export interface ITraceProductHistory {
    supplier: ITraceHistorySupplier | null;
    certification: ITraceHistoryCertification | null;
    warehouse: ITraceHistoryWarehouse | null;
    expedition: ITraceHistoryExpedition | null;
    tracking: ITraceHistoryTracking | null;
  }

  export class TraceProductHistory implements ITraceProductHistory {
    supplier: ITraceHistorySupplier | null = null;
    certification: ITraceHistoryCertification | null = null;
    warehouse: ITraceHistoryWarehouse | null = null;
    expedition: ITraceHistoryExpedition | null = null;
    tracking: ITraceHistoryTracking | null = null;

    constructor(data?: any) {
      if (!data) return;
      this.supplier = data.supplier ?? null;
      this.certification = data.certification ?? null;
      this.warehouse = data.warehouse ?? null;
      this.expedition = data.expedition ?? null;
      this.tracking = data.tracking ?? null;
    }
  }

  export interface ILoadTraceProductInput {
    art_qr: string;
    order_number: string;
  }
}
